import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router';

import { ShieldCheckIcon } from '@heroicons/react/24/solid';

import type { SetCookieOptions } from '~/hooks/useUtils/useCookies';
import type { RootLoaderData } from '~/root';
import logout from '~/utils/logout';
import toast from '~/utils/toast';

import Button from '../Button';
import SlideUpModal from '../Modal/SlideUpModal';

interface Props {
  justLoggedIn?: boolean;
  setCookie: (options: SetCookieOptions) => void;
}

const AdminBadge = ({ justLoggedIn, setCookie }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const handleLogout = () => {
    setCookie({ name: 'admin_logged_in', value: 'true', days: 0 });
    logout('admin_token');
  };

  useEffect(() => {
    if (justLoggedIn) {
      toast.success(t('login.success.admin'));
      setCookie({ name: 'admin_logged_in', value: 'true', days: 4 });
    }
  }, [justLoggedIn, setCookie]);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={
          'pointer-events-auto fixed right-3 top-14 z-40 flex rounded-lg bg-white p-2 shadow-xl ring-1 ring-black ring-opacity-5 lg:right-4'
        }
      >
        <ShieldCheckIcon
          className="h-4 w-4 text-wt-primary"
          style={{ color: site.publication.web_theme.primary_color }}
        />
      </button>
      <SlideUpModal
        title={`${t('admin.modal.title')}`}
        description={`${t('admin.modal.description')}`}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        backgroundColor={site.publication.web_theme.background_color}
        textColor={site.publication.web_theme.text_on_background_color}
        closeButtonBackgroundColor={site.publication.web_theme.secondary_color}
        closeButtonTextColor={site.publication.web_theme.text_on_secondary_color}
      >
        <div className="flex w-full justify-end space-x-2">
          <Button
            type="button"
            variant="ghost"
            className="shadow-none"
            onClick={() => {
              handleLogout();
              setIsModalOpen(false);
            }}
            style={{
              color: site.publication.web_theme.text_on_secondary_color,
              backgroundColor: site.publication.web_theme.secondary_color,
            }}
          >
            {t('shared.cta.logout')}
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => setIsModalOpen(false)}
            style={{
              color: site.publication.web_theme.text_on_primary_color,
              backgroundColor: site.publication.web_theme.primary_color,
            }}
          >
            {t('shared.cta.continue')}
          </Button>
        </div>
      </SlideUpModal>
    </>
  );
};

export default AdminBadge;
